import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, MessageSquareText, Trash2, Edit3, Megaphone, Loader2, PhoneCall, GraduationCap, X, UserPlus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import TemplatePicker from "@/components/TemplatePicker";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", tags: [], notes: "" };

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [massOpen, setMassOpen] = useState(false);
  const [massBody, setMassBody] = useState("");
  const [massSending, setMassSending] = useState(false);
  // Linked-phone (add another number for the same student)
  const [linkedOpen, setLinkedOpen] = useState(false);
  const [linkedContact, setLinkedContact] = useState(null);
  const [linkedPhone, setLinkedPhone] = useState("");
  const [linkedBusy, setLinkedBusy] = useState(false);
  // Manage classes dialog
  const [classesOpen, setClassesOpen] = useState(false);
  const [classesContact, setClassesContact] = useState(null);
  const [classesGroups, setClassesGroups] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [allClasses, setAllClasses] = useState([]);
  const [addClassId, setAddClassId] = useState("");
  const [addStudentName, setAddStudentName] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editingStudentValue, setEditingStudentValue] = useState("");
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/contacts", { params: q ? { q } : {} });
      setContacts(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [q]);

  const save = async () => {
    try {
      const payload = { ...form, tags: (form.tags || []).filter(Boolean) };
      if (editId) await api.put(`/contacts/${editId}`, payload);
      else await api.post("/contacts", payload);
      toast.success(editId ? "Contact updated" : "Contact added");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this contact?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (c) => {
    setForm({ first_name: c.first_name, last_name: c.last_name || "", phone: c.phone, email: c.email || "", tags: c.tags || [], notes: c.notes || "" });
    setEditId(c.id);
    setOpen(true);
  };

  const openMessage = async (c) => {
    // Ensure conversation exists then navigate
    try {
      const { data } = await api.get("/conversations");
      let conv = data.find((x) => x.contact_id === c.id);
      if (!conv) {
        // Send an empty request path: use send-message to auto-create? Instead just navigate; Inbox will create when message sent.
        // Simpler: we route to /inbox and user selects. Create via a stub call is unnecessary here.
      }
      if (conv) nav(`/inbox/${conv.id}`);
      else nav("/inbox");
    } catch { nav("/inbox"); }
  };

  const openLinked = (c) => {
    setLinkedContact(c);
    setLinkedPhone("");
    setLinkedOpen(true);
  };

  const openManageClasses = async (c) => {
    setClassesContact(c);
    setClassesGroups([]);
    setAddClassId("");
    setAddStudentName("");
    setClassesOpen(true);
    setClassesLoading(true);
    try {
      const [{ data: gs }, { data: cls }] = await Promise.all([
        api.get(`/contacts/${c.id}/enrollments`),
        allClasses.length ? Promise.resolve({ data: allClasses }) : api.get("/classes"),
      ]);
      setClassesGroups(gs);
      if (!allClasses.length) setAllClasses(cls);
      // Pre-fill student name if there's only one known student
      if (gs.length === 1) setAddStudentName(gs[0].student_name);
      else if ((c.students || [])[0]?.display_name) setAddStudentName(c.students[0].display_name);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClassesLoading(false); }
  };

  const dropEnrollment = async (enrollmentId, className, studentName) => {
    if (!confirm(`Remove ${studentName} from ${className}?`)) return;
    try {
      await api.delete(`/enrollments/${enrollmentId}`);
      toast.success(`${studentName} removed from ${className}`);
      const { data } = await api.get(`/contacts/${classesContact.id}/enrollments`);
      setClassesGroups(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addToClass = async () => {
    if (!addClassId) return toast.error("Pick a class");
    if (!addStudentName.trim()) return toast.error("Type the student name");
    setAddBusy(true);
    try {
      const { data } = await api.post(`/classes/${addClassId}/enroll`, {
        contact_id: classesContact.id,
        student_name: addStudentName.trim(),
      });
      if (data.already_enrolled) toast.info(`${addStudentName} was already in that class`);
      else toast.success(`Added ${addStudentName} to class`);
      setAddClassId("");
      const { data: fresh } = await api.get(`/contacts/${classesContact.id}/enrollments`);
      setClassesGroups(fresh);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddBusy(false); }
  };

  const saveRenameStudent = async (oldName) => {
    const newName = editingStudentValue.trim();
    if (!newName || newName === oldName) { setEditingStudent(null); return; }
    try {
      const { data } = await api.post("/students/rename", { contact_id: classesContact.id, old_name: oldName, new_name: newName });
      toast.success(`Renamed ${oldName} → ${newName} (${data.enrollments_updated} enrollment${data.enrollments_updated === 1 ? "" : "s"} updated)`);
      setEditingStudent(null);
      const { data: fresh } = await api.get(`/contacts/${classesContact.id}/enrollments`);
      setClassesGroups(fresh);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveLinked = async () => {
    if (!linkedContact) return;
    const student_ids = linkedContact.student_ids || (linkedContact.students || []).map((s) => s.id);
    if (!student_ids.length) {
      toast.error("This phone isn't linked to a student yet. Enroll them via Jackrabbit first.");
      return;
    }
    if (!linkedPhone.trim()) {
      toast.error("Enter the new phone number");
      return;
    }
    setLinkedBusy(true);
    try {
      const { data } = await api.post("/students/linked-phone", { student_ids, phone: linkedPhone.trim() });
      toast.success(`Added ${data.contact.phone} — now receives texts for ${data.contact.display_name}`);
      setLinkedOpen(false); setLinkedContact(null); setLinkedPhone("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLinkedBusy(false); }
  };


  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="students-page">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Students</h1>
            <p className="text-sm text-slate-500 mt-1">{(contacts || []).length} phone{(contacts || []).length === 1 ? "" : "s"} on file · shown by student name. One student can have multiple phones (mom + dad)</p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#D9232E] hover:bg-[#B91C25] text-white h-10" data-testid="add-contact-button">
                <Plus className="w-4 h-4 mr-2" /> Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="contact-dialog">
              <DialogHeader><DialogTitle>{editId ? "Edit Contact" : "New Contact"}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="contact-first-name" /></div>
                  <div><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="contact-last-name" /></div>
                </div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1..." data-testid="contact-phone" /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
                <div><Label>Tags (comma separated)</Label><Input value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="contact-tags" /></div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-contact-button">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="contacts-search-input"
            />
          </div>
          <Button variant="outline" onClick={() => setMassOpen(true)} className="ml-3" data-testid="mass-text-button">
            <Megaphone className="w-4 h-4 mr-2 text-[#D9232E]" /> Mass Text Everyone
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Tags</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="contacts-table">
              {(contacts || []).length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400">No contacts found</td></tr>
              )}
              {(contacts || []).map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">
                    <div>{c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || <span className="italic text-slate-400">(no name)</span>}</div>
                    {(c.students || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(c.students || []).map((st) => (
                          <button
                            key={st.id}
                            type="button"
                            onClick={() => nav(`/students?open=${encodeURIComponent(st.id)}`)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-100 text-[10px] font-medium text-[#8A1620] hover:bg-red-100 hover:ring-1 hover:ring-[#D9232E]/40"
                            data-testid={`contact-student-${st.id}`}
                            title="Open student detail"
                          >
                            <GraduationCap className="w-3 h-3" />
                            {st.name || st.display_name || "student"}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.phone}</td>
                  <td className="px-4 py-3 text-slate-600">{c.email || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {(c.tags || []).map((t) => (
                        <Badge key={t} variant="secondary" className="text-[10px] uppercase tracking-wide">{t}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openMessage(c)} title="Message" data-testid={`message-contact-${c.id}`}>
                        <MessageSquareText className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openManageClasses(c)} title="Manage classes" data-testid={`manage-classes-${c.id}`}>
                        <GraduationCap className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openLinked(c)}
                        title={(c.students || []).length ? `Add another phone for ${c.display_name}` : "Link this phone to a student first"}
                        disabled={!(c.students || []).length}
                        data-testid={`add-linked-phone-${c.id}`}
                      >
                        <PhoneCall className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)} title="Edit" data-testid={`edit-contact-${c.id}`}>
                        <Edit3 className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => del(c.id)} title="Delete" data-testid={`delete-contact-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-slate-500 hover:text-[#D9232E]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Manage classes for a contact */}
        <Dialog open={classesOpen} onOpenChange={(v) => { setClassesOpen(v); if (!v) { setClassesContact(null); setClassesGroups([]); } }}>
          <DialogContent className="max-w-2xl" data-testid="manage-classes-dialog">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <DialogTitle>Classes for {classesContact?.display_name || `${classesContact?.first_name || ""} ${classesContact?.last_name || ""}`}</DialogTitle>
                  <p className="text-sm text-slate-500">{classesContact?.phone} · Add or remove any class for this family's kids.</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => { setClassesOpen(false); openEdit(classesContact); }} className="shrink-0" data-testid="edit-parent-from-classes">
                  <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit parent
                </Button>
              </div>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto thin-scroll pr-1">
              {classesLoading ? (
                <div className="flex items-center justify-center py-10 text-slate-400 text-sm"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</div>
              ) : (classesGroups || []).length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">This family has no enrollments yet. Add one below.</div>
              ) : (
                <div className="space-y-4">
                  {(classesGroups || []).map((g) => (
                    <div key={g.student_name} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`student-group-${g.student_name}`}>
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between gap-2">
                        {editingStudent === g.student_name ? (
                          <div className="flex-1 flex items-center gap-1">
                            <Input
                              value={editingStudentValue}
                              onChange={(e) => setEditingStudentValue(e.target.value)}
                              className="h-7 text-sm bg-white"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") saveRenameStudent(g.student_name); if (e.key === "Escape") setEditingStudent(null); }}
                              data-testid={`rename-student-input-${g.student_name}`}
                            />
                            <Button size="sm" onClick={() => saveRenameStudent(g.student_name)} className="h-7 bg-[#D9232E] hover:bg-[#B91C25] text-white text-xs px-2">Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingStudent(null)} className="h-7 text-xs">✕</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="font-semibold text-sm text-slate-900 truncate">{g.student_name}</div>
                            <button type="button" onClick={() => { setEditingStudent(g.student_name); setEditingStudentValue(g.student_name); }} className="text-slate-400 hover:text-[#D9232E]" title="Rename student" data-testid={`rename-student-btn-${g.student_name}`}>
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                        <div className="text-xs text-slate-500 shrink-0">{(g.active || []).length} active · {(g.dropped || []).length} dropped</div>
                      </div>
                      {(g.active || []).length > 0 && (
                        <div className="divide-y divide-slate-100">
                          {(g.active || []).map((e) => (
                            <div key={e.enrollment_id} className="flex items-center justify-between px-4 py-2.5" data-testid={`active-class-${e.enrollment_id}`}>
                              <div className="min-w-0">
                                <div className="text-sm text-slate-900 truncate">{e.class_name}</div>
                                <div className="text-[11px] text-slate-500 flex gap-2">
                                  {e.location_code && <span className="font-mono">{e.location_code}</span>}
                                  {e.division && <span>· {e.division}</span>}
                                  {e.program && !e.division && <span>· {e.program}</span>}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => dropEnrollment(e.enrollment_id, e.class_name, g.student_name)}
                                className="text-slate-500 hover:text-[#D9232E] hover:bg-red-50 h-7 text-xs"
                                data-testid={`drop-class-${e.enrollment_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Drop
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      {(g.dropped || []).length > 0 && (
                        <details className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                          <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">Show {(g.dropped || []).length} dropped class{(g.dropped || []).length === 1 ? "" : "es"}</summary>
                          <ul className="mt-2 space-y-1 text-xs">
                            {(g.dropped || []).map((d) => (
                              <li key={d.enrollment_id} className="flex items-center gap-2 text-slate-500">
                                <X className="w-3 h-3 text-red-500" /> {d.class_name}
                                {d.dropped_at && <span className="text-[10px] text-slate-400">· {new Date(d.dropped_at).toLocaleDateString()}</span>}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Add-to-class form */}
              <div className="mt-4 border border-dashed border-slate-300 rounded-lg p-4 bg-white" data-testid="add-class-form">
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-4 h-4 text-[#D9232E]" />
                  <div className="text-sm font-semibold text-slate-900">Add to a class</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Class</Label>
                    <Select value={addClassId} onValueChange={setAddClassId}>
                      <SelectTrigger className="mt-1 h-9" data-testid="add-class-select"><SelectValue placeholder="Pick a class..." /></SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {(allClasses || []).map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}{c.location_code && c.location_code !== "Victoria" ? ` (${c.location_code})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Student name</Label>
                    <Input value={addStudentName} onChange={(e) => setAddStudentName(e.target.value)} placeholder="First Last" className="mt-1 h-9" data-testid="add-class-student-name" />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={addToClass} disabled={addBusy || !addClassId || !addStudentName.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-add-class">
                    {addBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add to class"}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={massOpen} onOpenChange={(v) => { setMassOpen(v); if (!v) setMassBody(""); }}>
          <DialogContent data-testid="mass-text-dialog">
            <DialogHeader>
              <DialogTitle>Mass Text Everyone</DialogTitle>
              <p className="text-sm text-slate-500">Sends an individual SMS to every contact ({contacts.length}). Replies come back individually.</p>
            </DialogHeader>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">{massBody.length} chars &middot; {(contacts || []).length} recipients</div>
              <TemplatePicker onSelect={(b) => setMassBody(b)} testId="mass-template-picker" />
            </div>
            <Textarea value={massBody} onChange={(e) => setMassBody(e.target.value)} placeholder="Type your message..." rows={5} data-testid="mass-text-body" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setMassOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!massBody.trim()) return;
                  setMassSending(true);
                  try {
                    const { data } = await api.post("/messages/broadcast-all", { body: massBody });
                    toast.success(`Sent to ${data.sent_count} contacts`);
                    setMassOpen(false); setMassBody("");
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setMassSending(false); }
                }}
                disabled={massSending || !massBody.trim()}
                className="bg-[#D9232E] hover:bg-[#B91C25] text-white"
                data-testid="mass-text-send"
              >
                {massSending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send to ${(contacts || []).length}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

